import React, { useState } from 'react';
import { SwatchIcon } from '@heroicons/react/24/outline';
import { THEMES, getTheme, applyTheme } from '../../theme';

/**
 * Appearance settings — switch the app's color theme.
 * Changing the dropdown applies the theme instantly (live preview) and persists it.
 */
const AppearanceTab = ({ onNotify }) => {
  const [theme, setTheme] = useState(getTheme());
  const active = THEMES.find((t) => t.id === theme) || THEMES[0];

  const handleChange = (id) => {
    setTheme(id);
    applyTheme(id);
    const t = THEMES.find((x) => x.id === id);
    onNotify && onNotify(`Theme changed to ${t ? t.name : id}`);
  };

  return (
    <div className="space-y-6">
      <div className="sm-card-static p-6">
        <div className="flex items-center gap-3 mb-1">
          <SwatchIcon className="h-6 w-6 text-blue-400" aria-hidden="true" />
          <h3 className="text-lg font-medium text-white">Theme</h3>
        </div>
        <p className="text-sm text-gray-400 mb-5">
          Choose a color theme for the whole app. Your choice is saved on this device.
        </p>

        <div className="max-w-sm">
          <label htmlFor="theme-select" className="block text-sm font-medium text-gray-300 mb-2">
            Color theme
          </label>
          <select
            id="theme-select"
            value={theme}
            onChange={(e) => handleChange(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {THEMES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} — {t.tagline}
              </option>
            ))}
          </select>
        </div>

        {/* Live preview of the selected theme */}
        <div className="mt-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Preview</p>
          <div className="rounded-xl border border-gray-700 overflow-hidden" style={{ background: active.swatches[0] }}>
            <div className="flex items-center gap-3 p-4 border-b border-gray-700/60">
              <div
                className="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundImage: active.gradient }}
              >
                <svg viewBox="0 0 24 24" fill="#fff" className="w-4 h-4" aria-hidden="true">
                  <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
                </svg>
              </div>
              <span className="text-sm font-semibold" style={{ color: active.swatches ? '#fff' : undefined }}>
                SageMindLabs
              </span>
              <span className="ml-auto text-xs text-gray-400">{active.name}</span>
            </div>
            <div className="p-4 flex items-center gap-3">
              <div className="rounded-lg px-3 py-2 text-sm flex-1" style={{ background: active.swatches[1], color: '#e6e9f2' }}>
                Surface card
              </div>
              <button
                type="button"
                className="rounded-lg px-4 py-2 text-sm font-medium text-white"
                style={{ background: active.swatches[2] }}
              >
                Primary
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Theme chooser cards (also clickable) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {THEMES.map((t) => {
          const isActive = t.id === theme;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => handleChange(t.id)}
              aria-pressed={isActive}
              className={`text-left rounded-xl border p-4 transition-colors ${
                isActive ? 'border-blue-500 bg-gray-800' : 'border-gray-700 bg-gray-800/60 hover:border-gray-600'
              }`}
            >
              <div className="flex gap-1.5 mb-3">
                {t.swatches.map((c, i) => (
                  <span key={i} className="h-6 flex-1 rounded" style={{ background: c }} />
                ))}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-white">{t.name}</span>
                {isActive && <span className="text-xs text-blue-400 font-medium">Active</span>}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{t.tagline}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AppearanceTab;
